import {
  NgxMatDatetimePickerModule,
  NgxMatTimepickerModule
} from '@angular-material-components/datetime-picker';
import { NgxMatMomentModule } from '@angular-material-components/moment-adapter';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { UiModule } from '@authority-labs/ui/ui.module';
import { PassChangeComponent } from '@check-list/shared/components/pass-change/pass-change.component';
import { UploadComponent } from '@check-list/shared/dialogs/upload/upload.component';
import { FileSizePipe } from '@check-list/shared/pipes/file-size/file-size.pipe';
import { TranslocoLocaleModule } from '@ngneat/transloco-locale';
import { LineChartModule, NumberCardModule } from '@swimlane/ngx-charts';
import { EditorModule } from '@tinymce/tinymce-angular';
import { he } from 'date-fns/locale';
import { DateFnsConfigurationService, DateFnsModule } from 'ngx-date-fns';
import { NgPipesModule } from 'ngx-pipes';
import { MaterialModule } from '../material.module';
import { NzModalConfirmCreateCompanyComponent } from '../modules/companies/company-details/company-details.component';
import { TranslocoRootModule } from '../modules/transloco/transloco-root.module';
import { NgZorroModule } from '../ng-zorro.module';
import { ActiveToggleComponent } from './components/active-toggle/active-toggle.component';
import { BaseDialogComponent } from './components/base-dialog/base-dialog.component';
import { BidsComponent } from './components/bids/bids.component';
import { ColumnFilterComponent } from './components/column-filter/column-filter.component';
import { CompanyInfoComponent } from './components/company-info/company-info.component';
import { ContactInfoComponent } from './components/contact-info/contact-info.component';
import { ContactsComponent } from './components/contacts/contacts.component';
import { ContractorInfoComponent } from './components/contractor-info/contractor-info.component';
import { ContractorSelectorComponent } from './components/contractor-selector/contractor-selector.component';
import { CreateReminderComponent } from './components/create-reminder/create-reminder.component';
import { CrudTableComponent } from './components/crud-table/crud-table.component';
import { DialogTitleComponent } from './components/dialog-title/dialog-title.component';
import { DragIconComponent } from './components/drag-icon/drag-icon.component';
import { EntityAsyncSelectorComponent } from './components/entity-async-selector';
import { FilesStorageComponent } from './components/files-storage/files-storage.component';
import { FireAlarmComponentInfoInfoComponent } from './components/fire-alarm-component-info/fire-alarm-component-info.component';
import { HeaderComponent } from './components/header/header.component';
import { MenuComponent } from './components/menu/menu.component';
import { NavbarComponent } from './components/navbar/navbar.component';
import { NotesComponent } from './components/notes/notes.component';
import { NotificationDateListComponent } from './components/notification-date-list/notification-date-list.component';
import { NotificationComponent } from './components/notification/notification.component';
import { NotificationsListComponent } from './components/notifications-list/notifications-list.component';
import { OrderInfoComponent } from './components/order-info/order-info.component';
import { PageTitleComponent } from './components/page-title/page-title.component';
import { PlannerInfoComponent } from './components/planner-info/planner-info.component';
import { PlannerSelectorComponent } from './components/planner-selector/planner-selector.component';
import { ProjectInfoComponent } from './components/project-info/project-info.component';
import { SearchComponent } from './components/search/search.component';
import { SubProjectInfoComponent } from './components/sub-project-info/sub-project-info.component';
import { TextEditorComponent } from './components/text-editor/text-editor.component';
import { UploadProgressComponent } from './components/upload-progress/upload-progress.component';
import { UploadQueueComponent } from './components/upload-queue/upload-queue.component';
import {
  AlertCompanyComponent,
  AlertCompanyOrdersComponent
} from './dialogs/alert-dialog/alert-dialog.component';
import { ApproveBidComponent } from './dialogs/approve-bid/approve-bid.component';
import { BidPreviewDialogComponent } from './dialogs/bid-preview-dialog/bid-preview-dialog.component';
import { CertificateDialogComponent } from './dialogs/certificate-dialog/certificate-dialog.component';
import { CreateCityDialogComponent } from './dialogs/city-dialog';
import { CompanyStatusesComponent } from './dialogs/company-statuses/company-statuses.component';
import { ComposeEmailDialogComponent } from './dialogs/compose-email-dialog/compose-email-dialog.component';
import { NzModalConfirmActionComponent } from './dialogs/confirm-action-dialog/confirm-action-dialog.component';
import { ContactReferencesDialogComponent } from './dialogs/contact-references-dialog/contact-references-dialog.component';
import { CreateContractorDialogComponent } from './dialogs/contractor-dialog/create-contractor-dialog.component';
import { CreateReminderDialogComponent } from './dialogs/create-reminder-dialog/create-reminder-dialog.component';
import { CustomNotesDialogComponent } from './dialogs/custom-notes-dialog/custom-notes-dialog.component';
import { DirectoryDialogComponent } from './dialogs/directory-dialog/directory-dialog.component';
import { NzModalEditTextComponent } from './dialogs/edit-text-dialog/edit-text-dialog.component';
import { EntitySelectorComponent } from './dialogs/entity-selector-dialog/entity-selector.component';
import { CreateFireAlarmComponentDialogComponent } from './dialogs/fire-alarm-component-dialog';
import { MainContactSelectorDialogComponent } from './dialogs/main-contact-selector-dialog/main-contact-selector-dialog.component';
import { NotesBankDialogComponent } from './dialogs/notes-bank-dialog/notes-bank-dialog.component';
import { NoticeComponent } from './dialogs/notice/notice.component';
import { OrderHistoryDialogComponent } from './dialogs/order-history-dialog/order-history-dialog.component';
import { OrderStatusesComponent } from './dialogs/order-statuses/order-statuses.component';
import { PasswordChangeDialogComponent } from './dialogs/password-change-dialog/password-change-dialog.component';
import { CreatePlannerDialogComponent } from './dialogs/planner-dialog/create-planner-dialog.component';
import { SubProjectHistoryDialogComponent } from './dialogs/sub-project-history-dialog/sub-project-history-dialog.component';
import { SubProjectOrdersComponent } from './dialogs/sub-project-orders-dialog/sub-project-orders-dialog.component';
import { WaterSupplyChartDialogComponent } from './dialogs/water-supply-chart-dialog/water-supply-chart-dialog.component';
import { AlertColorDirective } from './directives/alert-color.directive';
import { AutofocusDirective } from './directives/autofocus.directive';
import { EmailLinkDirective } from './directives/email-link.directive';
import { HasPermissionDirective } from './directives/has-permission.directive';
import { PagerLabelsDirective } from './directives/pager-labels.directive';
import { RedtColorDirective } from './directives/red-color.directive';
import { PendingChangesGuard } from './guards/pending-changes.guard';
import { FieldTypePipe } from './pipes/field-type/field-type.pipe';
import { HashPipe } from './pipes/hash/hash.pipe';
import { HighlightSearchPipe } from './pipes/highlihgt-search/highlight-search.pipe';
import { InspectionTypePipe } from './pipes/inspection-type/inspection-type.pipe';
import { NegativeNumberPipe } from './pipes/negative-number/negative-number.pipe';
import { OrderStatusPipe } from './pipes/order-status/order-status.pipe';
import { RolePipe } from './pipes/role/role.pipe';
import { SliceLimitPipe } from './pipes/slice-limit/slice-limit.pipe';
import { SubProjectStatusPipe } from './pipes/sub-project-status/sub-project-status.pipe';
import { UsernamePipe } from './pipes/username/username.pipe';

const fnsConfig = new DateFnsConfigurationService();
fnsConfig.setLocale(he);

@NgModule({
  imports: [
    RouterModule,
    CommonModule,
    MaterialModule,
    NgZorroModule,
    FormsModule,
    ReactiveFormsModule,
    UiModule,
    NgxMatMomentModule,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    DateFnsModule.forRoot(),
    TranslocoRootModule,
    DragDropModule,
    EditorModule,
    NgPipesModule,
    TranslocoLocaleModule,
    NumberCardModule,
    LineChartModule
  ],
  declarations: [
    HeaderComponent,
    PageTitleComponent,
    NavbarComponent,
    DragIconComponent,
    HashPipe,
    BidPreviewDialogComponent,
    PlannerSelectorComponent,
    CreatePlannerDialogComponent,
    ContractorSelectorComponent,
    CreateContractorDialogComponent,
    DirectoryDialogComponent,
    CrudTableComponent,
    NotesComponent,
    UsernamePipe,
    OrderStatusPipe,
    SubProjectStatusPipe,
    FieldTypePipe,
    InspectionTypePipe,
    SearchComponent,
    ContactsComponent,
    HighlightSearchPipe,
    DialogTitleComponent,
    BaseDialogComponent,
    ContactInfoComponent,
    EntitySelectorComponent,
    CompanyInfoComponent,
    PlannerInfoComponent,
    ContractorInfoComponent,
    TextEditorComponent,
    BidsComponent,
    UploadComponent,
    FilesStorageComponent,
    FileSizePipe,
    UploadQueueComponent,
    MenuComponent,
    UploadProgressComponent,
    PassChangeComponent,
    PasswordChangeDialogComponent,
    CreateReminderDialogComponent,
    NotificationsListComponent,
    NotificationComponent,
    NoticeComponent,
    ActiveToggleComponent,
    SliceLimitPipe,
    RolePipe,
    NotificationDateListComponent,
    ColumnFilterComponent,
    CustomNotesDialogComponent,
    ComposeEmailDialogComponent,
    ApproveBidComponent,
    ApproveBidComponent,
    CompanyStatusesComponent,
    OrderStatusesComponent,
    AlertColorDirective,
    AlertCompanyComponent,
    AlertCompanyOrdersComponent,
    NegativeNumberPipe,
    AutofocusDirective,
    NotesBankDialogComponent,
    WaterSupplyChartDialogComponent,
    MainContactSelectorDialogComponent,
    CertificateDialogComponent,
    CreateFireAlarmComponentDialogComponent,
    FireAlarmComponentInfoInfoComponent,
    RedtColorDirective,
    EmailLinkDirective,
    ProjectInfoComponent,
    SubProjectInfoComponent,
    OrderInfoComponent,
    EntityAsyncSelectorComponent,
    SubProjectOrdersComponent,
    NzModalConfirmActionComponent,
    CreateReminderComponent,
    NzModalConfirmCreateCompanyComponent,
    CreateCityDialogComponent,
    OrderHistoryDialogComponent,
    SubProjectHistoryDialogComponent,
    NzModalEditTextComponent,
    ContactReferencesDialogComponent,
    HasPermissionDirective,
    PagerLabelsDirective
  ],
  exports: [
    NgPipesModule,
    DragDropModule,
    CommonModule,
    MaterialModule,
    NgZorroModule,
    FormsModule,
    ReactiveFormsModule,
    HeaderComponent,
    PageTitleComponent,
    NavbarComponent,
    HashPipe,
    PlannerSelectorComponent,
    CreatePlannerDialogComponent,
    ContractorSelectorComponent,
    CreateContractorDialogComponent,
    DirectoryDialogComponent,
    NgxMatDatetimePickerModule,
    NgxMatTimepickerModule,
    NgxMatMomentModule,
    CrudTableComponent,
    DateFnsModule,
    NotesComponent,
    TranslocoRootModule,
    UsernamePipe,
    SliceLimitPipe,
    RolePipe,
    DatePipe,
    CurrencyPipe,
    OrderStatusPipe,
    SubProjectStatusPipe,
    FieldTypePipe,
    InspectionTypePipe,
    ContactsComponent,
    DialogTitleComponent,
    BaseDialogComponent,
    ContactInfoComponent,
    EntitySelectorComponent,
    CompanyInfoComponent,
    PlannerInfoComponent,
    ContractorInfoComponent,
    TextEditorComponent,
    BidsComponent,
    UploadComponent,
    FilesStorageComponent,
    FileSizePipe,
    UploadQueueComponent,
    UploadProgressComponent,
    PassChangeComponent,
    PasswordChangeDialogComponent,
    CreateReminderDialogComponent,
    NotificationsListComponent,
    NoticeComponent,
    ActiveToggleComponent,
    NotificationComponent,
    NotificationDateListComponent,
    ApproveBidComponent,
    AlertColorDirective,
    NegativeNumberPipe,
    AutofocusDirective,
    NotesBankDialogComponent,
    WaterSupplyChartDialogComponent,
    MainContactSelectorDialogComponent,
    CreateFireAlarmComponentDialogComponent,
    FireAlarmComponentInfoInfoComponent,
    RedtColorDirective,
    EmailLinkDirective,
    ProjectInfoComponent,
    SubProjectInfoComponent,
    OrderInfoComponent,
    EntityAsyncSelectorComponent,
    SubProjectOrdersComponent,
    NzModalConfirmActionComponent,
    CreateReminderComponent,
    UiModule,
    NzModalConfirmCreateCompanyComponent,
    CreateCityDialogComponent,
    OrderHistoryDialogComponent,
    SubProjectHistoryDialogComponent,
    NzModalEditTextComponent,
    DragIconComponent,
    HasPermissionDirective,
    PagerLabelsDirective
  ],
  providers: [
    { provide: DateFnsConfigurationService, useValue: fnsConfig },
    UsernamePipe,
    SliceLimitPipe,
    DatePipe,
    CurrencyPipe,
    OrderStatusPipe,
    SubProjectStatusPipe,
    FieldTypePipe,
    InspectionTypePipe,
    FileSizePipe,
    RolePipe,
    PendingChangesGuard,
    NegativeNumberPipe
  ]
})
export class SharedModule {}
